import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  BadgeCheck,
  ShieldCheck,
  Users,
  UserRound,
  TriangleAlert,
  PlusCircle,
  ClipboardList,
  Clock3,
  BarChart3,
  CheckCircle2,
  Trash2,
  Pencil,
  Award
} from 'lucide-react';
import { API } from '../../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
  CartesianGrid, Area   // <-- added for visual improvements
} from 'recharts';

const COLORS = ['#1a6b1a','#f5a623','#1565c0','#c8102e','#6b21a8'];

// Custom tooltip – cleaner look
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8,
      padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.82rem'
    }}>
      <p style={{ fontWeight: 600, margin: 0, color: 'var(--gray-700)' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '4px 0 0', color: p.color }}>
          {p.name}: {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([API.get('/stats'), API.get('/stats/admin')])
      .then(([s, a]) => { setStats(s.data); setAdminStats(a.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spin"/></div>;

  const clusterData = stats?.by_cluster ? [
    { name:'OVC',       value: parseInt(stats.by_cluster.ovc)||0 },
    { name:'KP',        value: parseInt(stats.by_cluster.kp)||0 },
    { name:'Health',    value: parseInt(stats.by_cluster.health)||0 },
    { name:'Gender',    value: parseInt(stats.by_cluster.gender)||0 },
    { name:'Advocacy',  value: parseInt(stats.by_cluster.advocacy)||0 },
  ].filter(d => d.value > 0) : [];   // hide clusters with zero

  const statCards = [
    {
      icon: Building2,
      label: 'Total Organisations',
      value: adminStats?.total || 0,
      iconBg: '#d4edda',
      change: '+8.4% from last month',
      up: true
    },
    {
      icon: BadgeCheck,
      label: 'Published',
      value: adminStats?.published || 0,
      iconBg: '#bfdbfe',
      change: `${
        adminStats?.published && adminStats?.total
          ? Math.round(adminStats.published / adminStats.total * 100)
          : 0
      }% of total`,
      up: true
    },
    {
      icon: ShieldCheck,
      label: 'Verified / Certified',
      value: adminStats?.certified || 0,
      iconBg: '#c7d2fe',
      change: '+5.7% from last month',
      up: true
    },
    {
      icon: UserRound,
      label: 'Women Led',
      value: stats?.women_led || 0,
      iconBg: '#fbcfe8',
      change: `${
        stats?.women_led && stats?.total_csos
          ? Math.round(stats.women_led / stats.total_csos * 100)
          : 0
      }% of total`,
      up: true
    },
    {
      icon: Users,
      label: 'Youth Led',
      value: stats?.youth_led || 0,
      iconBg: '#fde68a',
      change: `${
        stats?.youth_led && stats?.total_csos
          ? Math.round(stats.youth_led / stats.total_csos * 100)
          : 0
      }% of total`,
      up: true
    }
  ];

  return (
    <div className="anim-fadeIn">
      {/* Stat cards – unchanged */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:14, marginBottom:24 }}>
        {statCards.map((s,i) => {
          const IconComponent = s.icon;
          return (
            <div key={i} className="stat-card">
              <div className="stat-icon" style={{ background: s.iconBg }}>
                <IconComponent size={24} />
              </div>
              <div>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value" style={{ fontSize:'1.5rem' }}>{s.value.toLocaleString()}</div>
                <div className={`stat-change ${s.up?'up':'down'}`}>
                  {s.up ? '↗' : '↘'} {s.change}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Drafts alert – unchanged */}
      {adminStats?.drafts > 0 && (
        <div className="alert alert-warning" style={{ marginBottom:20 }}>
          <TriangleAlert size={18} style={{ marginRight:8 }} /> 
          <strong>{adminStats.drafts} organisations</strong> are saved as drafts and not visible to the public.
          <Link to="/admin/organisations?filter=draft" style={{ color:'inherit', fontWeight:700, marginLeft:8 }}>Review →</Link>
        </div>
      )}

      {/* Quick actions – unchanged */}
      <div className="card card-pad" style={{ marginBottom:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <h3 style={{ fontSize:'0.95rem', fontWeight:700 }}>Quick Actions</h3>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          <Link to="/admin/organisations/new" className="btn btn-primary">
            <PlusCircle size={18} /> Add Organisation
          </Link>
          <Link to="/admin/organisations" className="btn btn-secondary">
            <ClipboardList size={18} /> View All CSOs
          </Link>
          <Link to="/admin/organisations?filter=draft" className="btn btn-secondary">
            <Clock3 size={18} /> Review Drafts
          </Link>
          <Link to="/admin/organisations?filter=uncertified" className="btn btn-secondary">
            <BadgeCheck size={18} /> Pending Certification
          </Link>
          <Link to="/admin/reports" className="btn btn-secondary">
            <BarChart3 size={18} /> View Reports
          </Link>
        </div>
      </div>

      {/* Charts row 1 – Province + Cluster */}
      <div className="grid-2" style={{ marginBottom:24 }}>
        {/* By Province – enhanced */}
        <div className="card card-pad" style={{ background: '#fff' }}>
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:16 }}>CSOs by Province</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats?.by_province||[]} layout="vertical" margin={{ left: 10, right: 10 }}>
              <defs>
                <linearGradient id="provDashboardGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#2e7d32" stopOpacity={0.85} />
                  <stop offset="100%" stopColor="#1b5e20" stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize:11 }} />
              <YAxis type="category" dataKey="province" width={110} tick={{ fontSize:11 }} />
              <Tooltip content={<CustomTooltip formatter={v => `${v} CSOs`} />} />
              <Bar dataKey="count" fill="url(#provDashboardGrad)" radius={[0,4,4,0]} barSize={18} animationDuration={1200} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* By Cluster – enhanced */}
        <div className="card card-pad" style={{ background: '#fff' }}>
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:16 }}>Program Clusters</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <defs>
                {clusterData.map((_, idx) => (
                  <linearGradient key={`grad${idx}`} id={`pieDashboardGrad${idx}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={COLORS[idx % COLORS.length]} stopOpacity={0.9} />
                    <stop offset="100%" stopColor={COLORS[(idx+1) % COLORS.length]} stopOpacity={1} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={clusterData}
                cx="50%" cy="50%"
                innerRadius={45}
                outerRadius={80}
                paddingAngle={3}
                dataKey="value"
                animationDuration={1200}
                animationBegin={200}
                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={{ stroke: '#aaa', strokeWidth: 1 }}
              >
                {clusterData.map((_, idx) => (
                  <Cell key={idx} fill={`url(#pieDashboardGrad${idx})`} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip formatter={v => `${v} CSOs`} />} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, justifyContent:'center', marginTop:8 }}>
            {clusterData.map((d, idx) => (
              <span key={idx} style={{ fontSize:'0.7rem', display:'flex', alignItems:'center', gap:4 }}>
                <span style={{ width:10, height:10, borderRadius:'50%', background: COLORS[idx % COLORS.length] }} />
                {d.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Charts row 2 – Monthly Growth + Recent Activity */}
      <div className="grid-2" style={{ marginBottom:24 }}>
        {/* Monthly growth – enhanced */}
        <div className="card card-pad" style={{ background: '#fff' }}>
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:16 }}>Monthly Growth (12 months)</h3>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={stats?.monthly_growth||[]} margin={{ left: 10, right: 10 }}>
              <defs>
                <linearGradient id="lineDashboardGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize:10 }} tickFormatter={v => new Date(v).toLocaleDateString('en',{ month:'short' })} />
              <YAxis tick={{ fontSize:11 }} />
              <Tooltip content={<CustomTooltip labelFormatter={v => new Date(v).toLocaleDateString('en',{ month:'long', year:'numeric' })} formatter={v => `${v} new`} />} />
              <Area type="monotone" dataKey="count" fill="url(#lineDashboardGrad)" stroke="none" />
              <Line type="monotone" dataKey="count" stroke="#2e7d32" strokeWidth={2.5} dot={{ r:4, fill:'#2e7d32', strokeWidth:0 }} activeDot={{ r:6, fill:'#2e7d32' }} animationDuration={1500} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Recent activity – unchanged */}
        <div className="card card-pad">
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:16 }}>Recent Activity</h3>
          {stats?.recent_activity?.length ? (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {stats.recent_activity.slice(0,6).map((a,i) => {
                let ActivityIcon;
                if (a.action.includes('CREATE')) {
                  ActivityIcon = CheckCircle2;
                } else if (a.action.includes('DELETE')) {
                  ActivityIcon = Trash2;
                } else if (a.action.includes('CERTIFY')) {
                  ActivityIcon = Award;
                } else {
                  ActivityIcon = Pencil;
                }
                
                return (
                  <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:10, paddingBottom:10, borderBottom:i<5?'1px solid var(--gray-100)':'' }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--green-50)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.75rem', flexShrink:0 }}>
                      <ActivityIcon size={16} />
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'0.78rem', fontWeight:600, color:'var(--gray-700)' }}>
                        {a.user_name} — <span style={{ fontWeight:400, color:'var(--gray-500)' }}>{a.action.replace(/_/g,' ')}</span>
                      </div>
                      {a.record_name && <div style={{ fontSize:'0.73rem', color:'var(--gray-400)' }}>{a.record_name}</div>}
                      <div style={{ fontSize:'0.7rem', color:'var(--gray-400)' }}>{new Date(a.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <div className="empty-state"><div>No recent activity</div></div>}
        </div>
      </div>

      {/* Recent CSOs – unchanged */}
      {stats?.recent_csos?.length > 0 && (
        <div className="card card-pad">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
            <h3 style={{ fontSize:'0.9rem', fontWeight:700 }}>Recently Added Organisations</h3>
            <Link to="/admin/organisations" className="btn btn-sm btn-secondary">View All</Link>
          </div>
          <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
            {stats.recent_csos.map(cso => (
              <Link key={cso.id} to={`/admin/organisations/${cso.id}`}
                style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'var(--gray-50)', borderRadius:8, border:'1px solid var(--gray-200)', textDecoration:'none', transition:'all 0.2s' }}
                onMouseOver={e=>e.currentTarget.style.background='var(--green-50)'}
                onMouseOut={e=>e.currentTarget.style.background='var(--gray-50)'}>
                <div style={{ width:32, height:32, borderRadius:6, background:'var(--green-50)', border:'1px solid var(--green-100)', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'var(--green)', fontSize:'0.72rem' }}>
                  {(cso.acronym||cso.org_name).slice(0,2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize:'0.78rem', fontWeight:600, color:'var(--gray-800)' }}>{cso.org_name}</div>
                  <div style={{ fontSize:'0.7rem', color:'var(--gray-400)' }}>{new Date(cso.created_at).toLocaleDateString()}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}